/**
 * Toxic Cloud — Plan 4a Task 7.1
 *
 * AoE DoT item. Covers the caster's tile + all immediate neighbors for 3 rounds.
 * - Each affected player takes 5 damage when starting on or moving through the cloud (no key drop).
 * - Stone Skin absorbs the tick: consumed, item_blocked_stoneskin emitted, no damage.
 * - targetType: 'none' — no explicit target; uses graph neighbors at cast time.
 *
 * Self-registers via addItem(toxicCloud) at module load.
 */
import type { GameState } from '../types';
import type { ItemCastCtx, ItemCastResult, Item } from './types';
import type { ExecuteMovementOptions } from '../movement';
import { appendEvent } from '../event-log';
import { addItem } from './registry';
import { applyDamage } from '../damage';
import { applyBoardEntryEffects, registerBoardEntryEffect } from '../board-entry-effects';
import { tryBlockWithStoneSkin } from '../stone-skin-block';

export interface ToxicCloudDamageResult {
  damaged: boolean;
  blocked: boolean;
  died: boolean;
  cloudsHit: number;
}

export function pruneExpiredToxicClouds(state: GameState): void {
  state.activeClouds = (state.activeClouds ?? []).filter((cloud) => cloud.expiresAt >= state.round);
}

function hasCurrentTurnCasterGrace(
  state: GameState,
  playerId: string,
  cloud: GameState['activeClouds'][number],
): boolean {
  return (
    playerId === cloud.casterId &&
    cloud.createdRound === state.round &&
    cloud.createdTurnIndex === state.currentPlayerIndex
  );
}

export function applyToxicCloudDamageAtNode(
  state: GameState,
  playerId: string,
  nodeId: string,
): ToxicCloudDamageResult {
  pruneExpiredToxicClouds(state);

  let damaged = false;
  let blocked = false;
  let died = false;
  let cloudsHit = 0;

  for (const cloud of state.activeClouds ?? []) {
    if (!cloud.tileIds.includes(nodeId) || cloud.damagePerTick <= 0) continue;
    if (hasCurrentTurnCasterGrace(state, playerId, cloud)) continue;
    cloudsHit += 1;

    if (
      tryBlockWithStoneSkin(state, playerId, {
        actorId: cloud.casterId,
        sourceId: 'toxic_cloud',
      })
    ) {
      blocked = true;
      continue;
    }

    const result = applyDamage(
      state,
      playerId,
      cloud.damagePerTick,
      'item_toxic_cloud',
      cloud.casterId,
      true,
    );
    damaged = true;
    died = died || result.died;
    if (result.died) break;
  }

  return { damaged, blocked, died, cloudsHit };
}

export function withToxicCloudMovementDamage(
  state: GameState,
  options: ExecuteMovementOptions = {},
): ExecuteMovementOptions {
  const existingOnNodeEntered = options.onNodeEntered;
  return {
    ...options,
    onNodeEntered(playerId, fromNodeId, toNodeId, stepIndex, totalSteps) {
      const existingResult = existingOnNodeEntered?.(
        playerId,
        fromNodeId,
        toNodeId,
        stepIndex,
        totalSteps,
      );
      if (existingResult === 'stop') return 'stop';

      const result = applyBoardEntryEffects(state, playerId, toNodeId);
      return result.died ? 'stop' : existingResult;
    },
  };
}

export const toxicCloud: Item = {
  id: 'toxic_cloud',
  name: 'Toxic Cloud',
  description:
    "Releases a poisonous cloud covering the caster's tile and its neighbors. Players take 5 damage when they start in or move through the cloud for 3 rounds. Blocked by Stone Skin.",
  targetType: 'none',
  category: 'simple',
  rarity: 'uncommon',
  blockedByStoneSkin: true,

  cast(state: GameState, ctx: ItemCastCtx): ItemCastResult {
    const { casterId, graph } = ctx;

    // Require graph for neighbor computation
    if (!graph) {
      throw new Error('Toxic Cloud: cast requires ctx.graph to compute tile coverage');
    }

    // Validate caster
    const caster = state.players.get(casterId);
    if (!caster) throw new Error(`Toxic Cloud: caster not found: ${casterId}`);

    const casterNodeId = caster.nodeId;

    // Compute affected tiles: caster's tile + immediate graph neighbors
    const neighbors = graph.getNeighbors(casterNodeId);
    const tileIds: string[] = [casterNodeId, ...neighbors.map(({ node }) => node.id)];

    // Build the cloud entry and push onto state.activeClouds
    const cloud = {
      id: `cloud_${Date.now()}_${casterId}`,
      casterId,
      tileIds,
      damagePerTick: 5,
      expiresAt: state.round + 3,
      createdRound: state.round,
      createdTurnIndex: state.currentPlayerIndex,
    };
    state.activeClouds.push(cloud);

    // Emit item_cast
    appendEvent(state, {
      type: 'item_cast',
      playerId: casterId,
      casterId,
      itemId: 'toxic_cloud',
      targetNodeId: casterNodeId,
    });

    return { success: true };
  },
};

registerBoardEntryEffect('toxic_cloud', (state, playerId, nodeId) =>
  applyToxicCloudDamageAtNode(state, playerId, nodeId),
);

// Self-register at module load
addItem(toxicCloud);
